import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Search, LayoutGrid, List, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import plusicon from "../resources/blank_screen.png"
import icse from "../resources/icse.png"
import neet from "../resources/neet.png"
import jeeMains from "../resources/jeemains.png"
import jeeAdvance from "../resources/jeeAdvance.png"
import { getClassData } from './api'; // Adjust the import path as needed

const cn = (...classes) => classes.filter(Boolean).join(' ');

const ScrollArea = ({ children }) => (
  <div className="w-full overflow-auto">
    <div className="flex w-max space-x-4 p-4">
      {children}
    </div>
  </div>
);

export default function CreateExam() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState("grid");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [classData, setClassData] = useState(null);

  const templates = [
    {
      id: "blank",
      title: "Blank Exam",
      description: "Start from scratch",
      thumbnail: plusicon
    },
    {
      id: "cbse",
      title: "CBSE Template",
      description: "Standard CBSE exam format",
      thumbnail: "https://www.thegaudium.com/wp-content/uploads/2021/05/The_Gaudium_CBSE_Logo_2021_05.png?height=400&width=300"
    },
    {
      id: "icse",
      title: "ICSE Template",
      description: "Standard ICSE exam format",
      thumbnail: icse
    },
    {
      id: "neet",
      title: "NEET Template",
      description: "Medical entrance exam format",
      thumbnail: neet
    },
    {
      id: "jeeMains",
      title: "IIT-JEE Mains Template",
      description: "Engineering entrance exam format",
      thumbnail: jeeMains
    },
    {
      id: "jeeAdvance",
      title: "IIT-JEE Advance Template",
      description: "Advanced engineering entrance exam format",
      thumbnail: jeeAdvance
    }
  ];

  useEffect(() => {
    const fetchExams = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const classRoomId = localStorage.getItem('classRoomId');
        if (!classRoomId) {
          throw new Error('No classRoomId found in localStorage');
        }

        const response = await getClassData(classRoomId);
        if (response.statusCode === 200 && response.body && response.body.data) {
          const classData = response.body.data;
          setExams(classData.exams || []);
          setClassData(classData);
          setSelectedClass(classData.classValue || '');
          setSelectedSubject(classData.subject || '');
          setTeacherId(localStorage.getItem('teacherId') || '');
        } else {
          throw new Error('Invalid response from API');
        }
      } catch (err) {
        console.error('Error fetching exams:', err);
        setError(err.message);
        setExams([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExams();
  }, []);

  const handleNewExam = (template) => {
    localStorage.setItem('selectedTemplate', JSON.stringify(template));
    navigate('/exams/description');
  };

  const filteredExams = exams.filter(exam =>
    exam.exam_topic.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4 md:mb-0">Exam Creator</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search exams"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 shadow-sm"
            />
          </div>
          <button
            className="p-2 rounded-md border border-gray-300 hover:bg-gray-100"
            onClick={() => setView(view === "grid" ? "list" : "grid")}
          >
            {view === "grid" ? <LayoutGrid size={20} /> : <List size={20} />}
          </button>
          <button 
            onClick={() => handleNewExam(templates[0])}
            className="bg-blue-500 text-white px-6 py-2 rounded-full shadow-md hover:bg-blue-600 transition duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center"
          >
            <Plus size={20} className="mr-2" />
            Add Exam
          </button>
        </div>
      </div>

      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-800">Start a new exam</h2>
            <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">Template gallery</button>
          </div>
          <ScrollArea>
            {templates.map((template) => (
              <div
                key={template.id}
                className="w-[200px] space-y-3 cursor-pointer"
                onClick={() => handleNewExam(template)}
              >
                <div className="overflow-hidden rounded-md border">
                  <img
                    src={template.thumbnail}
                    alt={template.title}
                    className="aspect-[3/4] h-auto w-full object-cover transition-all hover:scale-105"
                  />
                </div>
                <div className="space-y-1 text-sm">
                  <h3 className="font-medium leading-none">{template.title}</h3>
                  <p className="text-xs text-gray-500">{template.description}</p>
                </div>
              </div>
            ))}
          </ScrollArea>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-800">Recent exams</h2>
            <div className="flex items-center space-x-2">
              <button className="text-sm text-gray-600 hover:text-gray-800 font-medium">Owned by anyone</button>
              <button className="text-sm text-gray-600 hover:text-gray-800 font-medium">Last opened</button>
            </div>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader className="animate-spin text-blue-500" size={48} />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <h2 className="text-2xl font-semibold text-red-600 mb-4">Error</h2>
              <p className="text-gray-700 mb-8">{error}</p>
            </div>
          ) : exams.length === 0 ? (
            <div className="text-center py-12">
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">You haven't created any exams yet.</h2>
              <p className="text-gray-500 mb-8">Click the "Add Exam" button to get started!</p>
            </div>
          ) : (
            <div className={cn(
              "grid gap-6",
              view === "grid" ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "grid-cols-1"
            )}>
              {filteredExams.map((exam) => (
                <div
                  key={exam.examId}
                  className={cn(
                    "bg-white rounded-lg shadow-md overflow-hidden transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg",
                    view === "list" && "flex items-center justify-between"
                  )}
                >
                  <div className={cn(
                    "p-6 flex flex-col items-center",
                    view === "list" && "flex-row justify-between w-full"
                  )}>
                    <div className={cn(
                      "mb-4 bg-blue-100 p-3 rounded-full",
                      view === "list" && "mb-0 mr-4"
                    )}>
                      <FileText size={40} className="text-blue-500" />
                    </div>
                    <div className={cn(
                      "text-center",
                      view === "list" && "text-left flex-grow"
                    )}>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{exam.exam_topic}</h3>
                      <p className="text-sm text-gray-500 mb-4">Duration: {exam.examDuration} mins</p>
                      <Link 
                        to={`/exam/details/${exam.examId}`} 
                        className="text-blue-500 hover:text-blue-600 transition duration-300 ease-in-out font-medium"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

